// @ts-nocheck
import axios from "axios";
import { store } from "../store/store";
import { logoutAction, refreshTokenAction } from "../store/actions/auth";
import { socket } from "./socket.io/socket.io.config";
import { ALERT_DIALOG } from "../store/constants/auth";
import { getDeviceId } from "./indexDb";
import { isNotVerifiedError, showVerifyFirstDialog } from "./verifyFirst";
// Centralized helper: uses VITE_API_URL_LOCAL in dev when set.
import { API_BASE_URL } from "../api/baseUrl";
import {
  GET_UNREAD_MESSAGES,
  GROUP_MESSAGE_DELIVERED,
  MESSAGE_DELIVERED,
  SET_USER_STATE,
} from "../customHooks/socketEvents/groupMessaging/constants";
import { getUnreadMessagesEventAction } from "../store/actions/groupMessagingActions";

const controller = new AbortController();

function abortControllerHandler() {
  controller?.forEach((c) => c.abort());
  controller?.clear();
}

// Warns only once per session when some caller still passes `version`.
// The backend no longer uses the /vN/ prefix — the parameter is ignored.
let _versionDeprecationWarned = false;

/**
 * Options accepted by `apiRequest`.
 *
 * Everything except `method`/`url` is optional. Without this type TypeScript infers
 * the destructured object literal and treats `data`, `params`, `version` and
 * `thirdPartyUrl` as REQUIRED, so any caller that omits them fails `tsc -b`. Every
 * existing caller happens to be a `@ts-nocheck` file, which is why it never showed.
 */
interface ApiRequestOptions {
  method: string;
  url: string;
  data?: unknown;
  isToken?: boolean;
  tempToken?: string | null;
  params?: Record<string, unknown>;
  file?: boolean;
  /** @deprecated the backend no longer uses a /vN/ prefix; passing it only logs a warning. */
  version?: string;
  thirdPartyUrl?: string;
}

const apiRequest = async ({
  method,
  url,
  data,
  isToken,
  tempToken = null,
  params,
  file = false,
  version,
  thirdPartyUrl,
}: ApiRequestOptions) => {
  if (version && !_versionDeprecationWarned) {
    _versionDeprecationWarned = true;
    console.warn(
      "[apiService] The deprecated `version` option was ignored. " +
        "The backend no longer uses /vN/ prefixes. Remove `version` from callers.",
    );
  }

  let response = null,
    error = null;
  const requestAuth = store.getState().auth;
  let options = {
    method: method,
    url: thirdPartyUrl || `${API_BASE_URL}/${url}`,
    headers: {
      Accept: "application/json",
    },
    data: data,
    params: params || {},
  };

  if (file) {
    options.responseType = "blob";
    options.headers = {
      ...options.headers,
      Accept:
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    };
  }

  if (isToken) {
    let token = requestAuth?.userToken;

    if (tempToken) {
      token = tempToken;
    }

    options = {
      ...options,
      headers: {
        ...options.headers,
        authorization: `${token}`,
      },
    };
  }
  options.signal = controller.signal;

  // Only invalidate the session that made this request. A late response must
  // never log out a replacement login or restore a session after sign-out.
  const isCurrentSession = () => {
    const current = store.getState().auth;
    return Boolean(current?.userToken) &&
      current?.userId === requestAuth?.userId &&
      current?.refreshToken === requestAuth?.refreshToken;
  };
  const forceSessionExpiredLogout = () => {
    if (!isCurrentSession()) return;
    store.dispatch(logoutAction({}));
    store.dispatch({
      type: ALERT_DIALOG,
      payload: {
        open: true,
        title: "Error!",
        message: "Your authentication session has expired. Please login again!",
      },
    });
  };

  try {
    response = await axios(options);
  } catch (err) {
    error = err;
    // Public requests and explicit capability tokens must not acquire the
    // signed-in user's identity through an automatic retry.
    const usesSession = isToken && !tempToken && !thirdPartyUrl && requestAuth?.userToken;
    if (err?.response?.status === 401 && usesSession && isCurrentSession()) {
      const refreshToken = requestAuth?.refreshToken;
      if (!refreshToken) {
        forceSessionExpiredLogout();
        return { response: null, error };
      }

      let newToken;
      try {
        const newResponse = await axios.post(
          `${API_BASE_URL}/refreshToken`,
          {
            refreshToken: refreshToken,
          },
          {
            signal: controller.signal,
          },
        );
        newToken = newResponse?.data?.token;
      } catch (refreshError) {
        error = refreshError;
        // 501 is the legacy invalid-refresh response. Transient server/network
        // failures still fail the operation, but do not revoke a valid login.
        if ([401, 403, 501].includes(refreshError?.response?.status)) {
          forceSessionExpiredLogout();
        }
        return { response: null, error };
      }

      if (!isCurrentSession()) return { response: null, error };
      if (typeof newToken !== "string" || !newToken.trim()) {
        forceSessionExpiredLogout();
        return { response: null, error };
      }
      store.dispatch(refreshTokenAction({ newToken }));
      options.headers.authorization = newToken;
      try {
        response = await axios(options);
        error = null;
      } catch (retryError) {
        error = retryError;
        // A renewed identity rejected by the server must sign in again.
        // Business/ownership/data errors remain failures of this operation.
        if (retryError?.response?.status === 401) forceSessionExpiredLogout();
      }
    }
    if (error && isNotVerifiedError(error)) showVerifyFirstDialog(error);
  }

  return { response, error };
};

// for upload image
const uploadImage = async ({ formData, isToken }) => {
  let response = null,
    error = null;

  let options = {
    method: "PATCH",
    url: `${API_BASE_URL}/updateProfile`,
    headers: {
      Accept: "application/json",
    },
    data: formData,
  };

  if (isToken) {
    let { auth } = store.getState();
    options = {
      ...options,
      headers: {
        ...options.headers,
        authorization: `${auth?.userToken}`,
      },
    };
  }
  options.signal = controller.signal;

  try {
    response = await axios(options);
  } catch (err) {
    console.error("API ERROR:::", err);
    error = err;
  }

  return { response, error };
};

// for upload image
const uploadFreelanceProfileImage = async ({ formData, isToken }) => {
  let response = null,
    error = null;

  let options = {
    method: "PUT",
    url: `${API_BASE_URL}/updateProfileImageFreelanceEc`,
    headers: {
      Accept: "application/json",
    },
    data: formData,
  };

  if (isToken) {
    let { auth } = store.getState();
    options = {
      ...options,
      headers: {
        ...options.headers,
        authorization: `${auth?.userToken}`,
      },
    };
  }

  options.signal = controller.signal;

  try {
    response = await axios(options);
  } catch (err) {
    console.error("API ERROR:::", err);
    error = err;
  }

  return { response, error };
};

// function to wait for socket to connect
const waitForSocketConnection = () =>
  new Promise((resolve, reject) => {
    if (socket.connected) return resolve();

    if (!socket.connecting) socket.connect();

    const timeout = setTimeout(() => {
      reject(new Error("Socket connection timeout"));
    }, 5000); // optional timeout

    socket.once("connect", () => {
      clearTimeout(timeout);
      resolve();
    });
  });

// Best-effort emit: never throws. On load / HMR / reconnect the socket may be
// momentarily disconnected; these events (heartbeat, delivered receipts, setup)
// re-fire on the next connect, so a transient "socket has been disconnected" is
// noise, not a real failure. Swallow it instead of leaking an unhandled rejection.
const safeEmitWithAck = async (event: string, payload: any) => {
  try {
    if (!socket.connected) return undefined;
    return await socket.emitWithAck(event, payload);
  } catch {
    return undefined;
  }
};

// for emitting socket events...
const emitSocketEvent = async ({
  event,
  payload,
  successCallback = () => {},
}) => {
  try {
    await waitForSocketConnection();
  } catch {
    // socket not up yet (load/HMR) — setup re-fires on next connect
    return undefined;
  }
  if (socket.connected) {
    const response = await safeEmitWithAck(event, payload);

    if (response?.status === "failure") {
      if (response?.error?.name === "TokenExpiredError") {
        let { refreshToken } = store.getState().auth;
        if (!refreshToken) return;
        try {
          const newResponse = await axios.post(
            `${API_BASE_URL}/refreshToken`,
            {
              refreshToken: refreshToken,
            },
          );
          const newToken = newResponse?.data?.token;

          store.dispatch(refreshTokenAction({ newToken }));

          const response = await safeEmitWithAck(event, payload);

          if (response?.status === 401) {
            store.dispatch({
              type: ALERT_DIALOG,
              payload: {
                open: true,
                title: "Error!",
                message:
                  "Your authentication session has expired. Please logout and login again!",
              },
            });
          }
        } catch (err) {
          console.error("API ERROR:::REFRESH TOKEN", err);
          store.dispatch({
            type: ALERT_DIALOG,
            payload: {
              open: true,
              title: "Error!",
              message: "Please try again!",
            },
          });
        }
      } else {
        return response;
      }
    } else {
      if (successCallback) {
        successCallback({ res: response });
      }
      return response;
    }
  } else {
    // store.dispatch({
    //   type: ALERT_DIALOG,
    //   payload: {
    //     open: true,
    //     title: "Error!",
    //     message:
    //       "Some error occured in connecting to the server. Please reload the browser!",
    //   },
    // });
  }
};

// every 60 seconds add device id and device type
const emitDeviceHeartBeat = async ({ event }) => {
  const userToken = store?.getState().auth?.userToken;
  const userId = store?.getState().auth?.userId;
  const deviceToken = store?.getState().auth?.deviceToken || null;
  const deviceId = getDeviceId();
  if (userToken && deviceId) {
    await safeEmitWithAck(event, {
      device: deviceId,
      token: userToken,
      deviceToken,
      userId,
    });
  }
};

/**
 * allow delivered for one on one chat room messages success
 */
const emitOneToOneMessagesDelivered = async ({ roomIds }) => {
  const userToken = store?.getState()?.auth?.userToken;
  if (userToken) {
    await safeEmitWithAck(MESSAGE_DELIVERED, {
      token: userToken,
      roomIds,
    });
  }
  store.dispatch(getUnreadMessagesEventAction());
};

/**
 * allow delivered for group chat room messages success
 */
const emitGroupChatMessagesDelivered = async ({ roomIds }) => {
  const userToken = store?.getState()?.auth?.userToken;
  if (userToken) {
    await safeEmitWithAck(GROUP_MESSAGE_DELIVERED, {
      token: userToken,
      roomIds,
    });
  }
  store.dispatch(getUnreadMessagesEventAction());
};

/**
 * only will be using this to store BACKGROUND state
 *
 * SET_USER_STATE(data)
data={userId,deviceId,state)
state=BACKGROUND or FOREGROUND
 */
const emitCurrentUserState = ({ state }) => {
  const userId = store?.getState()?.auth?.userId;
  // const state = "BACKGROUND";
  const deviceId = getDeviceId();

  void safeEmitWithAck(SET_USER_STATE, {
    userId,
    deviceId,
    state,
  });
};

export {
  apiRequest,
  uploadImage,
  emitSocketEvent,
  uploadFreelanceProfileImage,
  emitDeviceHeartBeat,
  emitOneToOneMessagesDelivered,
  emitGroupChatMessagesDelivered,
  emitCurrentUserState,
  controller,
  abortControllerHandler,
};
